import * as React from "react";
import { Shield, Wrench, Users, Target, CircleCheck, ArrowRight } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Link } from "@/components/common/Link";
const coreValues = [{
  title: "Unwavering Integrity",
  description: "We believe in honest pricing, transparent communication, and doing the right thing even when no one is watching.",
  icon: Shield
}, {
  title: "Exceptional Craftsmanship",
  description: "Our team of skilled professionals takes pride in every detail, ensuring your exterior stands the test of time.",
  icon: Wrench
}, {
  title: "Customer First",
  description: "Your satisfaction is our ultimate goal. We listen to your needs and tailor our solutions to fit your vision.",
  icon: Users
}, {
  title: "Reliable Protection",
  description: "We use only premium materials to safeguard your home against the harshest weather conditions.",
  icon: Target
}];
const teamMembers = [{
  name: "David Miller",
  role: "Founder & CEO",
  bio: "With over 20 years in the roofing industry, David founded Eagle Exteriors to bring a higher standard of quality to local homeowners.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/team_david_miller.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/team_david_miller.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/team_david_miller.png-wvc-sizes"
  }
}, {
  name: "Sarah Jenkins",
  role: "Lead Project Manager",
  bio: "Sarah ensures every project runs smoothly from initial consultation to final inspection, keeping clients informed every step of the way.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/team_sarah_jenkins.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/team_sarah_jenkins.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/team_sarah_jenkins.png-wvc-sizes"
  }
}, {
  name: "Michael Chang",
  role: "Master Roofer",
  bio: "Michael leads our installation crews with precision and expertise, specializing in complex architectural roofing systems.",
  image: {
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/team_michael_chang.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/team_michael_chang.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/team_michael_chang.png-wvc-sizes"
  }
}];
const milestones = ["Over 20 Years of Combined Experience", "Fully Licensed & Insured Professionals", "Hundreds of Satisfied Local Customers", "Premium Quality Materials Guaranteed"];
export default function AboutEagleExteriors() {
  return <section id="about-eagle-exteriors" className="bg-background py-16 md:py-24 lg:py-32">
      <div className="container mx-auto px-4 md:px-6">
     
        {}
        <div className="grid gap-12 lg:grid-cols-2 lg:gap-16 items-center mb-24">
          <div className="space-y-6">
            <Badge variant="secondary" className="bg-secondary/10 text-secondary hover:bg-secondary/20 border-none text-sm px-4 py-1">
              Our Story
            </Badge>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold font-default text-primary tracking-tight">
              Protecting Maryland & Northern Virginia Homes with <span className="text-secondary">Expert Roofing & Exterior Services</span>
            </h2>
            <div className="space-y-4 text-muted-foreground text-lg leading-relaxed">
              <p>
                We've been part of this community for over 20 years, Eagle Exteriors has built a reputation as the roofing partner trusted by neighbors across the region. We aren't just contractors; we are members of the community who understand that your home is your most valuable asset, deserving of the highest level of care and local expertise.
              </p>
              <p>
                Our commitment is rooted in two decades of local service. We provide top-tier exterior solutions to Montgomery County, Howard County, Frederick County, and Northern Virginia with the integrity and craftsmanship that only a long-standing local specialist can offer, ensuring your home remains protected for the next 20 years and beyond.
              </p>
            </div>
            
            <div className="pt-4">
              <ul className="grid sm:grid-cols-2 gap-4">
                {milestones.map((item, i) => <li key={i} data-index={i} className="flex items-start gap-3">
                    <CircleCheck className="size-5 text-secondary shrink-0 mt-0.5" />
                    <span className="text-foreground font-medium">{item}</span>
                  </li>)}
              </ul>
            </div>

            <div className="pt-6 flex flex-col sm:flex-row sm:items-center gap-4">
              <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
                <Link to="/ContactPage">
                  Work With Us <ArrowRight className="ml-2 size-4" />
                </Link>
              </Button>
              <Link to="/ServicesPage" className="text-primary font-medium hover:underline">
                → Explore our roofing & exterior services
              </Link>
            </div>
          </div>

          <div className="relative">
            <div className="absolute -inset-4 bg-secondary/10 rounded-2xl transform rotate-3 transition-transform hover:rotate-0 duration-500"></div>
            <div className="relative rounded-2xl overflow-hidden shadow-xl border border-border/50 bg-card">
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/eagle_exteriors_team_working.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/eagle_exteriors_team_working.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1660464/img/eagle_exteriors_team_working.png-wvc-sizes" alt="Eagle Exteriors team working on a roof" className="w-full h-auto object-cover aspect-[4/3]" loading="lazy" />
            </div>
          </div>
        </div>

        <Separator className="my-16 md:my-24 opacity-50" />

        {}
        <div className="mb-24">
          <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
            <h2 className="text-3xl md:text-4xl font-bold font-default text-primary">Our Core Values</h2>
            <p className="text-muted-foreground text-lg">
              These principles guide every decision we make and every nail we drive. They are the foundation of our reputation.
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {coreValues.map((value, i) => {
            const Icon = value.icon;
            return <Card key={i} data-index={i} className="border-border/50 shadow-sm hover:shadow-md transition-shadow bg-card">
                  <CardHeader>
                    <div className="size-12 rounded-lg bg-primary/5 flex items-center justify-center mb-4 text-primary">
                      <Icon className="size-6" />
                    </div>
                    <CardTitle className="text-xl font-default text-foreground">{value.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-muted-foreground leading-relaxed">
                      {value.description}
                    </p>
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>
 
        {}
        {}
        <div className="text-center py-16">
          <h2 className="text-3xl font-bold text-primary">
            Licensed & Insured • GAF Certified • Insurance Specialists • Storm Damage Experts
          </h2>

          <p className="text-muted-foreground mt-4 max-w-2xl mx-auto">
            Our credentials are backed by over 20 years of dedicated local service. We combine deep community roots with industry-leading certifications to provide the reliable, expert exterior care our neighbors have come to depend on.
          </p>
        </div>
      </div> 
    </section>;
}